import { findWorkflowStep } from '../../utils/Utils';
import { GroupUnion } from '../../models/GroupUnion';
import { Part } from '../../models/Api';
import { WorkflowSteps } from '../../models/WorkflowSteps';
import systemGroups from '../../services/GroupService';
import SystemService from '../../services/SystemService';
import UnitService from '../../services/UnitService';

export function getIncompleteLinkedPartsToSegment(segmentGroupId: number | undefined): string[] {
  const incompletePartsNames: string[] = [];
  const system = SystemService.getCurrentState().system;
  if (!system) return incompletePartsNames;

  const manifoldLinks = system.overlapGroupLinks?.filter((overlapGroupLink) => overlapGroupLink.intersectedGroupId === segmentGroupId);

  if (!manifoldLinks || manifoldLinks.length === 0) return incompletePartsNames;

  const getPartById = (id: string) => {
    return system.parts?.find((part) => part.id === id) as Part;
  };

  const linkedParkerPartIds = manifoldLinks
    .map((overlapGroupLink) => {
      const part = overlapGroupLink.linkedPartId && getPartById(overlapGroupLink.linkedPartId as string);
      if (part && part.isParkerPart) {
        return overlapGroupLink.linkedPartId;
      }
    })
    .filter((partId) => partId);

  const linkedNonParkerPartIds = manifoldLinks
    .map((overlapGroupLink) => {
      const part = overlapGroupLink.linkedPartId && getPartById(overlapGroupLink.linkedPartId as string);
      if (part && !part.isParkerPart) {
        return overlapGroupLink.linkedPartId;
      }
    })
    .filter((partId) => partId);

  if (linkedParkerPartIds.length + linkedNonParkerPartIds.length !== manifoldLinks.length) {
    return incompletePartsNames;
  }

  linkedParkerPartIds.forEach((id) => {
    const part = id && getPartById(id);
    if (part && findWorkflowStep(getPartById(id)) !== WorkflowSteps.MyProjects) {
      incompletePartsNames.push(part.name as string);
    }
  });

  linkedNonParkerPartIds.forEach((id) => {
    const part = id && getPartById(id);
    if (part && !part.dataItems?.find((dataItem) => dataItem.key === 'SummaryReportData')?.value) {
      incompletePartsNames.push(part.name as string);
    }
  });

  return incompletePartsNames;
}

export function getNonLinkedPartsToSegment(segmentGroupId: number | undefined): string[] {
  const nonLinkedPartsNames: string[] = [];
  const system = SystemService.getCurrentState().system;
  if (!system) return nonLinkedPartsNames;

  const manifoldLinks = system.overlapGroupLinks?.filter((overlapGroupLink) => overlapGroupLink.intersectedGroupId === segmentGroupId);

  if (!manifoldLinks || manifoldLinks.length === 0) return nonLinkedPartsNames;

  const getPartById = (id: string) => {
    return system.parts?.find((part) => part.id === id) as Part;
  };

  const nonLinkedPartIds = manifoldLinks
    .map((overlapGroupLink) => {
      if (!overlapGroupLink.linkedPartId) {
        return overlapGroupLink.intersectedPartId;
      }
    })
    .filter((partId) => partId);

  nonLinkedPartIds.forEach((id) => {
    const part = id && getPartById(id);
    if (part) {
      nonLinkedPartsNames.push(part.name as string);
    }
  });
  return nonLinkedPartsNames;
}

export function allManifoldLinkedPartsAreConfigured(manifoldGroup: GroupUnion): boolean {
  const sysState = SystemService.getCurrentState();
  const sys = sysState.system;
  if (!sys) return false;

  const unitDefinitions = UnitService.getCurrentState();
  const groups = systemGroups(sys, unitDefinitions);

  const segmentGroup = groups.find(
    (group) => group.definition.uniqueName === 'PneumaticManifoldSegment' && group.instance.parentGroupId === manifoldGroup.instance.id,
  );

  return getIncompleteLinkedPartsToSegment(segmentGroup?.instance.id).length === 0;
}

export function currentManifoldWorkflowStep(manifoldGroup: GroupUnion): WorkflowSteps {
  const sysState = SystemService.getCurrentState();
  const sys = sysState.system;
  if (!sys) return WorkflowSteps.Add;

  const unitDefinitions = UnitService.getCurrentState();
  const groups = systemGroups(sys, unitDefinitions);

  const segmentGroup = groups.find(
    (group) => group.definition.uniqueName === 'PneumaticManifoldSegment' && group.instance.parentGroupId === manifoldGroup.instance.id,
  );
  const manifoldLinks = sys.overlapGroupLinks?.filter((overlapGroupLink) => overlapGroupLink.intersectedGroupId === segmentGroup?.instance.id);

  if (!manifoldLinks || manifoldLinks.length === 0) return WorkflowSteps.Add;
  const valve = sys.parts?.find((part) => part.id === manifoldLinks[0].intersectedPartId);
  if (!valve) return WorkflowSteps.Add;
  return findWorkflowStep(valve);
}

export function isPartMountedOnManifold(part: Part): boolean {
  const system = SystemService.getCurrentState().system;
  const segmentGroup = system?.groups?.find((group) => group.id === part.groupInstanceId);
  const segmentGroupDefinition = system?.groupDefinitions?.find((groupDefinition) => groupDefinition.id === segmentGroup?.definitionId);
  const isSegmentGroup = (segmentGroup && segmentGroupDefinition?.uniqueName === 'PneumaticManifoldSegment') ?? false;

  return isSegmentGroup;
}
